<template>
  <FormKit id="changePassword" ref="formRef" v-model="formData" name="changePassword" type="form" @submit="onSave">
    <FormStepInputs
      :step="{
        stepName: name,
        formInputs: content,
      }"
    >
      <template #errorMessage>
        <div v-if="showApiErrorMessage" class="md:max-w-[656px] mb-8">
          <InformationBox
            title=""
            class="flex items-start gap-4 p-5 px-6 self-stretch"
            :description="errorMessage.content.value"
            :type="errorMessage.messageType.value"
            :isCircle="true"
            :useButtonSlot="false"
          ></InformationBox>
        </div>
      </template>
      <template #newPassword>
        <div class="flex flex-col gap-8 md:gap-6 md:max-w-[312px]">
          <PasswordInput
            ref="newPasswordRef"
            :key="newPasswordKey"
            :newPasswordLabel="newPassword.label.value"
            :confirmPasswordLabel="confirmPassword.label.value"
            :inputLabel="newPassword.label.value"
            :showRequirements="true"
            @valid="(e: string) => onValidNewEmailEntered(e)"
            @invalid="(e: string) => onInvalidNewEmailEntered(e)"
          ></PasswordInput>
        </div>
      </template>
      <template #passwordNoMatchMessage>
        <div v-if="showPasswordMismatch" class="flex flex-col gap-8 md:gap-6 md:max-w-[312px]">
          <InformationBox
            :title="passwordNoMatchMessage.title.value"
            :description="passwordNoMatchMessage.content.value"
            :type="passwordNoMatchMessage.messageType.value"
            :isCircle="true"
            :useButtonSlot="false"
          ></InformationBox>
        </div>
      </template>
    </FormStepInputs>
    <div class="flex w-[640px] mt-12 items-start content-start gap-4 flex-wrap">
      <Link :field="discardChangesLink?.link" class="btn btn-tertiary btn-md">
        <div class="flex items-center space-x-2">
          <img src="@/assets/icons/arrow-left.svg" alt="left arrow" class="w-5 h-5 inline-block" />
          <span>{{ discardChangesLink?.link?.value?.text }}</span>
        </div>
      </Link>
      <button type="submit" :disabled="!hasChanges" class="btn btn-md btn-primary">
        {{ getFormInput(props.content, 'saveButtonText')?.label?.value ?? 'Change password' }}
      </button>
    </div>
    <div class="!mt-10 mb-10 md:!mt-12 md:!mb-12">
      <Divider></Divider>
    </div>
    <FullScreenSpinner v-if="showSaving" :applyOpacity="true"></FullScreenSpinner>
  </FormKit>
</template>
<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { ISitecoreFormGenericInput } from '@/interfaces';
import FormStepInputs from '@/components/FormStepInputs/FormStepInputs.vue';
import { getFormInput } from '@/utils/form-input-helper';
import PasswordInput from '@/components/PasswordInput/PasswordInput.vue';
import FullScreenSpinner from '@/components/FullScreenSpinner/FullScreenSpinner.vue';
import { v4 } from 'uuid';
import { ToastAlertType } from '@/enums/toast-alert-enum';
import { useToastAlertStore } from '@/store/toast-alert/toast-alert.state';
import { useAccountStore, useAnalyticsStore } from '@/store';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import { PageAnalyticsBuilder } from '@/utils/page-analytics-builder';
import { useI18n } from 'vue-i18n';
import { FormEvents } from '@/enums/form-events.enum';
import Divider from '@/components/Divider/Divider.vue';
import { Link } from '@sitecore-jss/sitecore-jss-vue';

const failedToastMessage = 'Unable to save changes';

const getSetPasswordErrorMessage = (error: any): string => {
  const data = error?.response?.data;

  if (data && typeof data === 'object' && 'message' in data) {
    const apiMessage = data.message;
    if (typeof apiMessage === 'string' && apiMessage.trim().length > 0) {
      return apiMessage;
    }
  } else if (typeof data === 'string' && data.trim().length > 0) {
    return data;
  }

  return failedToastMessage;
};

const accountStore = useAccountStore();
const toastAlertStore = useToastAlertStore();
const analyticsStore = useAnalyticsStore();

const props = defineProps<{
  content: ISitecoreFormGenericInput[];
  id: string;
  name: string;
}>();

const newPasswordRef = ref<any>(null);
const newPasswordKey = ref<string>('');
const confirmPasswordKey = ref<string>('');

const newPassword = ref(getFormInput(props.content, 'newPassword'));
const confirmPassword = ref(getFormInput(props.content, 'confirmPassword'));
const passwordNoMatchMessage = ref(getFormInput(props.content, 'passwordNoMatchMessage'));
const errorMessage = ref(getFormInput(props.content, 'errorMessage'));
const discardChangesLink = ref(getFormInput(props.content, 'discardChangesLink'));

const model = ref<any>({ currentPassword: '', newPassword: '' });

const formRef = ref<any>(null);
const showSaving = ref<boolean>(false);
const showPasswordMismatch = ref<boolean>(false);
const showApiErrorMessage = ref<boolean>(false);
const hasChanges = ref<boolean>(true); //default to true so button is enabled as per reqs

const dictionary = useI18n().getLocaleMessage('en') as Record<string, string>;

const formData = ref({});

onMounted(() => {
  newPasswordKey.value = v4();
  confirmPasswordKey.value = v4();

  //Fixes styling issue when page content is short
  const mainElement = document.querySelector('main');
  if (mainElement) {
    mainElement.classList.add('grow-0');
  }
  pushAnalytics();
});

const onValidNewEmailEntered = (e: string) => {
  showPasswordMismatch.value = false;
  model.value.newPassword = e;
};

const onInvalidNewEmailEntered = (e: string) => {
  showPasswordMismatch.value = false;
};

const onSave = async () => {
  try {
    showApiErrorMessage.value = false;

    showPasswordMismatch.value = newPasswordRef.value?.password !== newPasswordRef.value?.password2;
    if (showPasswordMismatch.value) {
      return;
    }

    if (!accountStore.identityDetails?.email) {
      toastAlertStore.addToastAlert({
        title: failedToastMessage,
        description: '',
        type: ToastAlertType.ERROR,
        duration: 6000,
      });
      return;
    }

    showSaving.value = true;

    await accountStore
      .setPassword(model.value.newPassword, true)
      .then(async () => {
        toastAlertStore.addToastAlert({
          title: 'Password changed successfully',
          description: 'You will receive a confirmation email shortly.',
          type: ToastAlertType.SUCCESS,
          duration: 6000,
        });

        hasChanges.value = false;
        clearFields();

        showSaving.value = false;

        await analyticsStore.pushAnalytics({
          event: FormEvents.COMPLETE,
          form: {
            name: 'Change password',
            stepName: 'FormCompleted',
          },
        });
      })
      .catch(async (e) => {
        console.error(e);
        showSaving.value = false;
        const setPasswordErrorMessage = getSetPasswordErrorMessage(e);

        clearFields();
        toastAlertStore.addToastAlert({
          title: failedToastMessage,
          description: setPasswordErrorMessage,
          type: ToastAlertType.ERROR,
          duration: 6000,
        });

        showApiErrorMessage.value = setPasswordErrorMessage.toLowerCase().indexOf('password') > -1;

        await analyticsStore.pushAnalytics({
          event: FormEvents.JEOPARDY,
          form: {
            name: 'Change password',
            formErrorType: failedToastMessage,
            formErrorDetail: setPasswordErrorMessage,
          },
        });
      });
  } catch (error) {
    showSaving.value = false;
    const setPasswordErrorMessage = getSetPasswordErrorMessage(error);
    toastAlertStore.addToastAlert({
      title: failedToastMessage,
      description: setPasswordErrorMessage,
      type: ToastAlertType.ERROR,
      duration: 6000,
    });

    await analyticsStore.pushAnalytics({
      event: FormEvents.JEOPARDY,
      form: {
        name: 'Change password',
        formErrorType: failedToastMessage,
        formErrorDetail: setPasswordErrorMessage,
      },
    });
  }
};

const clearFields = () => {
  showApiErrorMessage.value = false;

  if (newPasswordRef.value?.password) {
    newPasswordRef.value.password = '';
  }

  if (newPasswordRef.value?.password2) {
    newPasswordRef.value.password2 = '';
  }

  if (formRef.value) {
    formRef.value?.node?.reset();
  }
};

const pushAnalytics = () => {
  const pageAnalytics = new PageAnalyticsBuilder();
  pageAnalytics.populateProducts(dictionary);
  pageAnalytics.pushAnalytics();
};
</script>
